import { Fragment, useMemo, useRef, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { cn, getInitials } from '@/lib/utils';
import { formatearFechaHora } from '@/lib/tareas';
import { useAsignables } from '@/hooks/useTareas';
import { useComentarioMutations, useComentarios } from '@/hooks/useTareasDetalle';
import type { ComentarioTarea, TareaDetalle, UsuarioAsignable } from '@/types/tareas';

const campo = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 resize-y';
const MAX_COMENTARIO = 5000;
const MAX_SUGERENCIAS = 6;
const MAX_LARGO_CONSULTA = 30;

const nombreCompleto = (u: Pick<UsuarioAsignable, 'nombre' | 'apellidoPaterno'>) =>
  [u.nombre, u.apellidoPaterno].filter(Boolean).join(' ');

/** Minúsculas y sin acentos, para comparar "jose" con "José". */
const normalizar = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const escapar = (t: string) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Resalta las menciones (`@Nombre`) que el backend reconoció en el comentario. */
function TextoConMenciones({ texto, menciones }: { texto: string; menciones: { id: string; nombre: string }[] }) {
  const partes = useMemo(() => {
    if (!menciones.length) return [texto];
    const nombres = [...menciones].sort((a, b) => b.nombre.length - a.nombre.length).map(m => escapar(`@${m.nombre}`));
    return texto.split(new RegExp(`(${nombres.join('|')})`, 'g'));
  }, [texto, menciones]);
  const esMencion = (parte: string) => menciones.some(m => `@${m.nombre}` === parte);

  return (
    <>
      {partes.map((p, i) => (esMencion(p)
        ? <span key={i} className="font-medium text-primary-700 bg-primary-50 rounded px-0.5">{p}</span>
        : <Fragment key={i}>{p}</Fragment>))}
    </>
  );
}

export function ComentariosPanel({ tarea }: { tarea: TareaDetalle }) {
  const { data: comentarios = [], isLoading } = useComentarios(tarea.id);
  const mut = useComentarioMutations(tarea.id, tarea.tableroId);

  return (
    <div className="max-w-2xl space-y-4">
      {isLoading && <p className="text-sm text-text-secondary">Cargando…</p>}
      {!isLoading && comentarios.length === 0 && <p className="text-sm text-text-secondary">Aún no hay comentarios.</p>}

      <ul className="space-y-4">
        {comentarios.map(c => <Comentario key={c.id} comentario={c} mut={mut} />)}
      </ul>

      <Redactor pendiente={mut.crear.isPending} onEnviar={(contenido, menciones, alExito) => mut.crear.mutate({ contenido, menciones }, { onSuccess: alExito })} />
    </div>
  );
}

/** Caja de nuevo comentario con menciones: al escribir `@` ofrece a las personas del equipo. */
function Redactor({
  pendiente, onEnviar,
}: { pendiente: boolean; onEnviar: (contenido: string, menciones: string[], alExito: () => void) => void }) {
  const { data: usuarios = [] } = useAsignables();
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const [texto, setTexto] = useState('');
  const [elegidos, setElegidos] = useState<Map<string, string>>(new Map());
  const [consulta, setConsulta] = useState<{ inicio: number; texto: string } | null>(null);
  const [indice, setIndice] = useState(0);

  const sugerencias = useMemo(() => {
    if (!consulta) return [];
    const buscado = normalizar(consulta.texto);
    return usuarios.filter(u => normalizar(nombreCompleto(u)).includes(buscado)).slice(0, MAX_SUGERENCIAS);
  }, [consulta, usuarios]);

  const detectar = (valor: string, cursor: number) => {
    const antes = valor.slice(0, cursor);
    const m = new RegExp(`(^|\\s)@([^\\s@]{0,${MAX_LARGO_CONSULTA}})$`).exec(antes);
    setConsulta(m ? { inicio: cursor - m[2].length - 1, texto: m[2] } : null);
    setIndice(0);
  };

  const elegir = (u: UsuarioAsignable) => {
    if (!consulta || !areaRef.current) return;
    const cursor = areaRef.current.selectionStart;
    const nombre = nombreCompleto(u);
    const nuevo = `${texto.slice(0, consulta.inicio)}@${nombre} ${texto.slice(cursor)}`;
    const posicion = consulta.inicio + nombre.length + 2;
    setTexto(nuevo);
    setElegidos(prev => new Map(prev).set(u.id, nombre));
    setConsulta(null);
    requestAnimationFrame(() => {
      areaRef.current?.focus();
      areaRef.current?.setSelectionRange(posicion, posicion);
    });
  };

  const enviar = () => {
    const contenido = texto.trim();
    if (!contenido) return;
    // Solo se mandan las menciones cuyo `@Nombre` sigue en el texto (si se borró, ya no aplica).
    const menciones = [...elegidos].filter(([, nombre]) => contenido.includes(`@${nombre}`)).map(([id]) => id);
    onEnviar(contenido, menciones, () => { setTexto(''); setElegidos(new Map()); setConsulta(null); });
  };

  const alTeclear = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (sugerencias.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setIndice(i => (i + 1) % sugerencias.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setIndice(i => (i - 1 + sugerencias.length) % sugerencias.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); elegir(sugerencias[indice]); return; }
    }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); enviar(); }
  };

  return (
    <div className="relative">
      <textarea
        ref={areaRef}
        value={texto}
        onChange={e => { setTexto(e.target.value); detectar(e.target.value, e.target.selectionStart); }}
        onKeyDown={alTeclear}
        onClick={e => detectar(texto, e.currentTarget.selectionStart)}
        onBlur={() => setConsulta(null)}
        rows={3}
        maxLength={MAX_COMENTARIO}
        placeholder="Escribe un comentario… usa @ para mencionar a alguien (Ctrl+Enter para enviar). Se almacena cifrado."
        className={campo}
      />

      {sugerencias.length > 0 && (
        <ul role="listbox" className="absolute z-10 mt-1 w-72 max-w-full rounded-lg border border-gray-200 bg-white shadow-lg p-1">
          {sugerencias.map((u, i) => (
            <li key={u.id} role="option" aria-selected={i === indice}>
              {/* onMouseDown evita que el textarea pierda el foco antes de elegir. */}
              <button
                type="button"
                onMouseDown={e => { e.preventDefault(); elegir(u); }}
                className={cn('w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm', i === indice ? 'bg-primary-50' : 'hover:bg-gray-50')}
              >
                <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                  {getInitials(nombreCompleto(u))}
                </span>
                <span className="truncate">{nombreCompleto(u)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex justify-end mt-2">
        <button onClick={enviar} disabled={!texto.trim() || pendiente} className="px-4 py-1.5 rounded-lg bg-primary-500 text-white text-sm font-medium disabled:opacity-50">
          Comentar
        </button>
      </div>
    </div>
  );
}

function Comentario({ comentario, mut }: { comentario: ComentarioTarea; mut: ReturnType<typeof useComentarioMutations> }) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(comentario.contenido);
  const [confirmando, setConfirmando] = useState(false);

  const guardar = () => {
    const contenido = texto.trim();
    if (!contenido || contenido === comentario.contenido) { setEditando(false); return; }
    mut.editar.mutate({ id: comentario.id, contenido }, { onSuccess: () => setEditando(false) });
  };

  return (
    <li className="flex gap-3">
      <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex items-center justify-center overflow-hidden flex-shrink-0">
        {comentario.autorFotoUrl
          ? <img src={comentario.autorFotoUrl} alt="" className="w-full h-full object-cover" />
          : getInitials(comentario.autorNombre)}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm">
          <span className="font-semibold text-text-primary">{comentario.autorNombre}</span>{' '}
          <span className="text-xs text-text-secondary">
            {formatearFechaHora(comentario.createdAt)}{comentario.editadoAt && ' · editado'}
          </span>
        </p>

        {editando ? (
          <div className="mt-1">
            <textarea value={texto} onChange={e => setTexto(e.target.value)} rows={3} maxLength={MAX_COMENTARIO} className={campo} autoFocus />
            <div className="flex gap-2 mt-2">
              <button onClick={guardar} disabled={mut.editar.isPending} className="px-3 py-1 rounded-lg bg-primary-500 text-white text-sm disabled:opacity-50">Guardar</button>
              <button onClick={() => { setTexto(comentario.contenido); setEditando(false); }} className="px-3 py-1 rounded-lg border border-gray-200 text-sm">Cancelar</button>
            </div>
          </div>
        ) : (
          <p className="mt-0.5 text-sm text-text-primary whitespace-pre-wrap break-words">
            <TextoConMenciones texto={comentario.contenido} menciones={comentario.menciones} />
          </p>
        )}

        {comentario.esMio && !editando && (
          <div className="mt-1 flex items-center gap-3 text-xs text-text-secondary">
            <button onClick={() => setEditando(true)} className="inline-flex items-center gap-1 hover:text-primary-600"><Pencil size={11} /> Editar</button>
            {confirmando ? (
              <span className="flex items-center gap-2">
                <span>¿Eliminar?</span>
                <button onClick={() => mut.eliminar.mutate(comentario.id)} className="text-red-600 font-medium">Sí</button>
                <button onClick={() => setConfirmando(false)}>No</button>
              </span>
            ) : (
              <button onClick={() => setConfirmando(true)} className="inline-flex items-center gap-1 hover:text-red-500"><Trash2 size={11} /> Eliminar</button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
